/**
 * Response layout for LSEG's AdFin functions: how LSEG Workspace's Excel
 * add-in (Refinitiv for Office 26.30.2) turns an analytics answer into the
 * grid it shows. Ported from BaseResponseProcessor (63223) and
 * BaseAdFinResProcessor (15646), with the helpers they use (getOutputFields,
 * capitalize, getShallowStringify, DisplayOptionVariants) and the error text of LSEG's executor
 * (ErrorMessageGenerator, SyncBatchRequestExecutor).
 *
 * Only the path without a Dest is laid out here: the formula cell's value
 * (`callerValue`) plus the blocks around it. Placing the grid at a Dest is
 * the static writer's job.
 */

import { AdFinInputContract } from "./baseProcessor";

/** One block of cells, placed relative to the formula cell. */
export interface PositionedData {
  position: { row: number; column: number };
  data: unknown[][];
}

/** What a response processor gives Excel: the formula cell's value and the blocks around it. */
// LSEG: BaseResponseProcessor_63223.clientFormat (its result)
export interface ClientResponse {
  callerValue: unknown;
  positionedData: PositionedData[];
}

/** A response processor's column: its name ("!x" required, "?x" optional) and LSEG's type label. */
export interface ResponseHeader {
  name: string;
  type: string;
}

/** Rows by request id, one value per field, before the display options lay them out. */
export interface FieldValues {
  requestIDs: unknown[];
  fields: string[];
  valueSets: unknown[][];
}

/** An error as LSEG's response processors receive it: `{ error: {...} }`. */
export interface ResponseError {
  message?: string;
  status?: string;
  errors?: { key?: string; reason: string }[];
}

// LSEG: responseProcessorConfig_84625.errorMessages.invalidDisplayOptions
export const INVALID_DISPLAY_OPTIONS = "Invalid display options!";

// LSEG: DisplayOptionVariants_78755.DisplayOptionVariants
const DISPLAY_OPTION_VARIANTS = {
  rHidCHfd: ["", "rh:id", "ch:fd", "rh:id ch:fd", "rh:id ch=fd", "rh=id ch:fd", "ch:fd rh:id", "ch:fd rh=id", "ch=fd rh:id", "rh=id", "ch=fd", "rh=id ch=fd", "ch=fd rh=id"],
  rHfdCHid: ["rh:fd", "ch:id", "rh:fd ch:id", "rh:fd ch=id", "rh=fd ch:id", "ch:id rh:fd", "ch:id rh=fd", "ch=id rh:fd", "rh=fd", "ch=id", "rh=fd ch=id", "ch=id rh=fd"],
  rHidfd: ["rh:id;fd", "rh:fd;id", "rh=id;fd", "rh=fd;id"],
  cHidfd: ["ch:id;fd", "ch:fd;id", "ch=id;fd", "ch=fd;id"],
  rHdtCHid: ["rh:dt", "ch:id", "rh:dt ch:id", "rh:dt ch=id", "rh=dt ch:id", "ch:id rh:dt", "ch:id rh=dt", "ch=id rh:dt", "rh=dt", "ch=id", "rh=dt ch=id", "ch=id rh=dt"],
  rHidCHdt: ["rh:id", "ch:dt", "rh:id ch:dt", "rh:id ch=dt", "rh=id ch:dt", "ch:dt rh:id", "ch:dt rh=id", "ch=dt rh:id", "rh=id", "ch=dt", "rh=id ch=dt", "ch=dt rh=id"],
};

// The option texts each layout reads (LSEG: BaseResponseProcessor_63223 constructor, the lists inside each function).
// LSEG: BaseResponseProcessor_63223.rHidCHfdFunc
const FIELDS_HEADER_ROW = [
  "ch:fd", "rh:id ch:fd", "ch:fd rh:id", "ch:dt", "rh:id ch:dt", "ch:dt rh:id",
  "ch=fd", "rh=id ch=fd", "ch=fd rh=id", "ch=dt", "rh=id ch=dt", "ch=dt rh=id",
  "rh:id ch=fd", "rh=id ch:fd", "ch:fd rh=id", "ch=fd rh:id", "rh:id ch=dt", "rh=id ch:dt", "ch:dt rh=id", "ch=dt rh:id",
];
const IDS_HEADER_COLUMN = [
  "rh:id", "rh:id ch:fd", "ch:fd rh:id", "rh:id ch:dt", "ch:dt rh:id",
  "rh=id", "rh=id ch=fd", "ch=fd rh=id", "rh=id ch=dt", "ch=dt rh=id",
  "rh:id ch=fd", "rh=id ch:fd", "ch:fd rh=id", "ch=fd rh:id", "rh:id ch=dt", "rh=id ch:dt", "ch:dt rh=id", "ch=dt rh:id",
];
// LSEG: BaseResponseProcessor_63223.rHfdCHidFunc ("rd:dt" is LSEG's spelling)
const IDS_HEADER_ROW = [
  "ch:id", "rh:fd ch:id", "ch:id rh:fd", "rh:dt ch:id", "ch:id rd:dt",
  "ch=id", "rh=fd ch=id", "ch=id rh=fd", "rh=dt ch=id", "ch=id rd=dt",
];
const FIELDS_HEADER_COLUMN = [
  "rh:fd", "rh:fd ch:id", "ch:id rh:fd", "rh:dt", "rh:dt ch:id", "ch:id rh:dt",
  "rh=fd", "rh=fd ch=id", "ch=id rh=fd", "rh=dt", "rh=dt ch=id", "ch=id rh=dt",
];

// --- getOutputFields --------------------------------------------------------------

// LSEG: DisplayOptionVariants_78755.OutputFieldType
enum OutputFieldType {
  regular,
  required,
  optional,
}

interface OutputField {
  name: string;
  nameLC: string;
  type: OutputFieldType;
}

interface ExposeState {
  exposeAllColumns: boolean;
  exposeAlsoOptionalColumns: boolean;
  doNotExposeAllColumns: boolean;
  explicitExposeAllColumns: boolean;
  explicitIndex: number;
}

/**
 * The columns to show: the user's Fields (`requested`) against the
 * processor's headers (`available`, "!x" always shown, "?x" only on request).
 * Blank or "*" is every header; "+x" adds x to all, "-x" removes it; a plain
 * list is shown as given, with the required headers added.
 */
// LSEG: flattenArray1Level_75285.getOutputFields
export function getOutputFields(requested: string[], available: string[]): string[] {
  const requestedLC = new Set<string>();
  const excludedLC = new Set<string>();
  let out: string[] = [];
  if (!requested.length || (requested.length === 1 && requested[0] === "")) requested = ["*"];
  const fields: OutputField[] = available.map((name) => {
    let type = OutputFieldType.regular;
    if (name.startsWith("!")) {
      type = OutputFieldType.required;
      name = name.substring(1);
    } else if (name.startsWith("?")) {
      type = OutputFieldType.optional;
      name = name.substring(1);
    }
    return { name, nameLC: name.toLowerCase(), type };
  });
  const state: ExposeState = {
    exposeAllColumns: false,
    exposeAlsoOptionalColumns: false,
    doNotExposeAllColumns: false,
    explicitExposeAllColumns: false,
    explicitIndex: -1,
  };
  updateRequestedFields(state, requested, out, excludedLC, requestedLC);
  if (state.doNotExposeAllColumns) state.exposeAllColumns = false;
  if (!state.exposeAllColumns) return reorderOutputFields(fields, out, state, excludedLC);
  out = [];
  fields.forEach((f) => {
    if (!isHidden(excludedLC, requestedLC, f, state)) out.push(f.name);
  });
  return out;
}

// LSEG: flattenArray1Level_75285.validateSet
function isHidden(excludedLC: Set<string>, requestedLC: Set<string>, f: OutputField, state: ExposeState): boolean {
  return (
    isSetIncludes(excludedLC, f.nameLC) ||
    (f.type === OutputFieldType.optional && !state.exposeAlsoOptionalColumns && !requestedLC.has(f.nameLC))
  );
}

// LSEG: flattenArray1Level_75285.reorderOutputFields
function reorderOutputFields(fields: OutputField[], out: string[], state: ExposeState, excludedLC: Set<string>): string[] {
  let insertAt = 0;
  fields.forEach((f) => {
    const found = out.findIndex((name) => {
      const lc = name.toLowerCase();
      return lc === f.nameLC || f.nameLC.startsWith(`${lc}.`);
    });
    if (found !== -1) {
      if (insertAt < found + 1) insertAt = found + 1;
      return;
    }
    if ((!state.explicitExposeAllColumns && f.type !== OutputFieldType.required) || isSetIncludes(excludedLC, f.nameLC)) return;
    if (state.explicitIndex !== -1) out.splice(state.explicitIndex++, 0, f.name);
    else out.splice(insertAt++, 0, f.name);
  });
  return out;
}

// LSEG: flattenArray1Level_75285.updateRequestedFields
function updateRequestedFields(
  state: ExposeState,
  requested: string[],
  out: string[],
  excludedLC: Set<string>,
  requestedLC: Set<string>,
): void {
  requested.forEach((field) => {
    if (field === "") {
      out.push(field);
      return;
    }
    if (field === "+*" || field === "*+" || field === "+") {
      field = "*";
      state.exposeAlsoOptionalColumns = true;
    }
    if (field === "*") {
      if (!state.exposeAllColumns) {
        state.exposeAllColumns = true;
        state.explicitExposeAllColumns = true;
        state.explicitIndex = out.length;
      }
      return;
    }
    let lc = field.toLowerCase();
    let exclude = false;
    switch (field[0]) {
      case "+":
        state.exposeAllColumns = true;
        field = field.substring(1);
        lc = lc.substring(1);
        break;
      case "-":
        state.exposeAllColumns = true;
        exclude = true;
        field = field.substring(1);
        lc = lc.substring(1);
        break;
      default:
        state.doNotExposeAllColumns = true;
    }
    if (exclude) {
      excludedLC.add(lc);
      requestedLC.delete(lc);
    } else {
      excludedLC.delete(lc);
      if (!requestedLC.has(lc)) {
        requestedLC.add(lc);
        out.push(field);
      }
    }
  });
}

// LSEG: flattenArray1Level_75285.isSetIncludes
function isSetIncludes(set: Set<string>, nameLC: string): boolean {
  if (!nameLC.includes(".")) return set.has(nameLC);
  let found = false;
  set.forEach((s) => {
    if (nameLC === s || nameLC.startsWith(`${s}.`)) found = true;
  });
  return found;
}

// LSEG: toCamelCase_44181.capitalize
export function capitalize(s: string): string {
  return (s[0] ?? "").toUpperCase() + s.slice(1);
}

/**
 * A value as short text for an error message: two levels of objects
 * ("{a:1, b:{...}}"), one of arrays ("[1, [...]]"), texts quoted.
 */
// LSEG: toCamelCase_44181.getShallowStringify
export function getShallowStringify(v: unknown, [arrayDepth, objectDepth]: [number, number] = [0, 0]): string {
  if (v === null || v === undefined) return `${v}`;
  if (Array.isArray(v)) {
    if (v.length === 0) return "[]";
    return arrayDepth < 1 ? `[${v.map((x) => getShallowStringify(x, [arrayDepth + 1, objectDepth])).join(", ")}]` : "[...]";
  }
  if (typeof v === "object") {
    const keys = Object.keys(v);
    if (keys.length === 0) return "{}";
    return objectDepth <= 1
      ? `{${keys.map((k) => `${k}:${getShallowStringify((v as Record<string, unknown>)[k], [arrayDepth, objectDepth + 1])}`).join(", ")}}`
      : "{...}";
  }
  return typeof v === "string" ? `'${v}'` : `${v}`;
}

// --- error texts ------------------------------------------------------------------

/** An API error as LSEG's executor reads it. */
export interface ApiError {
  message?: string;
  code?: unknown;
  id?: unknown;
}

const DEFAULT_MESSAGE = "API error";
const VALIDATION_ERROR_MESSAGE = "Validation error";
const INVALID_INPUT_MESSAGE = "invalid input";
const INVALID_INPUT_CODE = "invalidinput";
const ADINTERP_ERROR_CODE = "adinterp_";
const MAX_MESSAGE_LENGTH = 250;

/**
 * The text LSEG shows for a failed request: "API error: <message>, id: <id>",
 * or the validation forms ("Validation error..."), the message cut at 250 characters.
 */
// LSEG: ErrorMessageGenerator (39198).generateMessage
export function errorMessage(e: ApiError): string {
  const code = `${e.code ?? ""}`;
  const id = e.id ? `${e.id}` : "";
  let text = DEFAULT_MESSAGE;
  let withId = true;
  if (e.message) {
    const message = e.message.length > MAX_MESSAGE_LENGTH ? `${e.message.substring(0, MAX_MESSAGE_LENGTH - 4)} ...` : e.message;
    [text, withId] = validationErrorMessage(message, text, code);
  }
  if (withId && id) text += `, id: ${id}`;
  if (id.toLowerCase().startsWith(ADINTERP_ERROR_CODE)) text = e.message || `AdInterp error: ${id.substring(ADINTERP_ERROR_CODE.length)}`;
  return text;
}

// LSEG: ErrorMessageGenerator (39198).getValidationErrorMessage
function validationErrorMessage(message: string, text: string, code: string): [string, boolean] {
  const lc = message.toLowerCase();
  if (lc.startsWith(VALIDATION_ERROR_MESSAGE.toLowerCase())) return [message, false];
  if (lc.startsWith(INVALID_INPUT_MESSAGE)) return [`${VALIDATION_ERROR_MESSAGE}${message.substring(INVALID_INPUT_MESSAGE.length)}`, false];
  if (code.toLowerCase().startsWith(INVALID_INPUT_CODE)) return [`${VALIDATION_ERROR_MESSAGE}: ${message}`, false];
  return [`${text}: ${message}`, true];
}

/**
 * The error an item of a batched answer becomes, as LSEG's batch executor
 * builds it: its message ("Server error" when none), code (500 when none) and id.
 */
// LSEG: SyncBatchRequestExecutor (80918), its RequestorError helper
export function requestorError(e: ApiError | undefined): ApiError {
  return { message: e?.message || "Server error", code: e?.code ?? 500, id: e?.id ?? "" };
}

// --- the processors ---------------------------------------------------------------

/** One of the display options' layouts: the field values as rows, with the headers the option asks for. */
type Layout = (data: FieldValues, option: string) => unknown[][];

// LSEG: BaseResponseProcessor_63223.BaseResponseProcessor
export abstract class BaseResponseProcessor {
  // LSEG: BaseResponseProcessor_63223.rHidCHfdFunc
  protected readonly rHidCHfd: Layout = (data, option) => {
    const fieldsRow = FIELDS_HEADER_ROW.includes(option);
    const idsColumn = IDS_HEADER_COLUMN.includes(option);
    const header = idsColumn ? ["", ...data.fields] : data.fields;
    const rows = data.requestIDs.map((id, i) => (idsColumn ? [id, ...data.valueSets[i]] : data.valueSets[i]));
    return fieldsRow ? [header, ...rows] : rows;
  };

  // LSEG: BaseResponseProcessor_63223.rHfdCHidFunc
  protected readonly rHfdCHid: Layout = (data, option) => {
    const idsRow = IDS_HEADER_ROW.includes(option);
    const fieldsColumn = FIELDS_HEADER_COLUMN.includes(option);
    const header = fieldsColumn ? ["", ...data.requestIDs] : data.requestIDs;
    const rows = data.fields.map((field, i) => {
      const values = data.valueSets.map((set) => set[i]);
      return fieldsColumn ? [field, ...values] : values;
    });
    return idsRow ? [header, ...rows] : rows;
  };

  // LSEG: BaseResponseProcessor_63223.rHidfdFunc
  protected readonly rHidfd: Layout = (data, option) => {
    const idFirst = option === "rh:id;fd" || option === "rh=id;fd";
    const rows: unknown[][] = [];
    data.requestIDs.forEach((id, i) =>
      data.fields.forEach((field, j) => rows.push([idFirst ? id : field, idFirst ? field : id, data.valueSets[i][j]])),
    );
    return rows;
  };

  // LSEG: BaseResponseProcessor_63223.cHidfdFunc
  protected readonly cHidfd: Layout = (data, option) => {
    const idFirst = option === "ch:id;fd" || option === "ch=id;fd";
    const ids: unknown[] = [];
    const fields: unknown[] = [];
    const values: unknown[] = [];
    data.requestIDs.forEach((id, i) => {
      data.fields.forEach((field) => {
        ids.push(id);
        fields.push(field);
      });
      values.push(...data.valueSets[i]);
    });
    return [idFirst ? ids : fields, idFirst ? fields : ids, values];
  };

  /**
   * The display option (the CH/RH words of Options, lower-cased and joined
   * by a space, e.g. "rh:id ch:fd") and whether it names a date axis.
   * [A] LSEG's third value, showInvalidField, is not ported: no date function
   * reads it, and LSEG reads it from a group its pattern lacks, so it fails on "showInvalidField:y".
   */
  // LSEG: BaseResponseProcessor_63223.getDisplayOptions
  getDisplayOptions(options: string, idName = "id", fieldName = "fd"): [string, boolean] {
    const display = (options.match(/(ch|rh)[:=][\w;]+/gi) || []).join(" ").toLowerCase();
    return [display.replace(idName, "id").replace(fieldName, "fd"), /dt/i.test(display)];
  }

  /** The values laid out by the display option; an unknown option throws "Invalid display options!". */
  // LSEG: BaseResponseProcessor_63223.rhChFormat
  rhChFormat(data: FieldValues, option: string): unknown[][] {
    const v = DISPLAY_OPTION_VARIANTS;
    const layouts: [string[], Layout][] = [
      [v.rHidCHfd, this.rHidCHfd],
      [v.rHfdCHid, this.rHfdCHid],
      [v.rHidfd, this.rHidfd],
      [v.cHidfd, this.cHidfd],
      [v.rHdtCHid, this.rHfdCHid],
      [v.rHidCHdt, this.rHidCHfd],
    ];
    const layout = layouts.find(([variants]) => variants.includes(option));
    if (!layout) throw new Error(INVALID_DISPLAY_OPTIONS);
    return layout[1](data, option);
  }

  /**
   * Rows and columns swapped when Options says TRANSPOSE:Y (the default
   * argument transposes). [A] An empty grid stays empty (LSEG would fail on it).
   */
  // LSEG: BaseResponseProcessor_63223.transposeData
  transposeData(data: unknown[][], options = "transpose=y"): unknown[][] {
    if (!/transpose[:=]y/i.test(options) || !data.length) return data;
    return data[0].map((_, c) => data.map((row) => row[c]));
  }

  /**
   * The grid as Excel gets it without a Dest: its top-left value in the
   * formula cell, the rest of its first row to the right, the other rows below.
   */
  // LSEG: BaseResponseProcessor_63223.clientFormat (the path without a Dest)
  clientFormat(data: unknown[][]): ClientResponse {
    const rows = this.modifyDataDates(data);
    const first = rows.shift() ?? [null];
    const callerValue = first.shift() ?? null;
    const positionedData: PositionedData[] = [];
    if (first.length) positionedData.push({ position: { row: 0, column: 1 }, data: [first] });
    if (rows.length) positionedData.push({ position: { row: 1, column: 0 }, data: rows });
    return { callerValue, positionedData };
  }

  /** "YYYY-MM-DDT00:00:00Z" loses its Z. */
  // LSEG: BaseResponseProcessor_63223.modifyDataDates
  modifyDataDates(data: unknown[][]): unknown[][] {
    return data.map((row) =>
      row.map((v) => {
        if (typeof v !== "string") return v;
        const t = v.trim();
        return /^\d{4}-\d{2}-\d{2}T00:00:00Z$/i.test(t) ? t.substring(0, t.length - 1) : v;
      }),
    );
  }

  /** "" for an empty value and for any text containing "null" (any case); a boolean as "TRUE" / "FALSE". */
  // LSEG: BaseResponseProcessor_63223.getValueIfEmpty (BaseAdFinResProcessor overrides it, without the booleans)
  getValueIfEmpty(v: unknown): unknown {
    if (String(v) === "" || v === null || v === undefined || /null/i.test(String(v))) return "";
    return v === true ? "TRUE" : v === false ? "FALSE" : v;
  }

  /** The error's message in the formula cell, its reasons (if any) one per row below. */
  // LSEG: BaseResponseProcessor_63223.getErrorResponse
  getErrorResponse(e: { error: ResponseError }): ClientResponse {
    const reasons = (e.error.errors ?? []).filter((x) => x.reason !== "").map((x) => x.reason);
    const rows = this.transposeData([reasons]);
    const out: ClientResponse = { callerValue: e.error.message, positionedData: [] };
    if (rows.length) out.positionedData = [{ position: { row: 1, column: 0 }, data: rows }];
    return out;
  }

  /** The rows below the first (the block at column 0) lose their empty rows. */
  // LSEG: BaseResponseProcessor_63223.removeEmptyRows
  removeEmptyRows(response: ClientResponse, falseIsValue?: boolean): ClientResponse {
    response.positionedData = response.positionedData.map((block) =>
      block.data && block.position.column === 0
        ? { ...block, data: block.data.filter((row) => !this.checkIfIsEmptyRow(row, falseIsValue)) }
        : block,
    );
    if (response.positionedData.length === 2 && response.positionedData[1].data.length === 0) {
      response.positionedData = response.positionedData.slice(0, 1);
    }
    return response;
  }

  // LSEG: BaseResponseProcessor_63223.checkIfIsEmptyRow
  checkIfIsEmptyRow(row: unknown[], falseIsValue?: boolean): boolean {
    for (const v of row) {
      if (falseIsValue && v === false) return false;
      if (v) return false;
    }
    return true;
  }

  /** Per header: its name without the !/? mark, that lower-cased, and whether it is optional. */
  // LSEG: BaseResponseProcessor_63223.getHeaderResponse
  getHeaderResponse(headers: ResponseHeader[]): [string, string, boolean][] {
    return headers.map((h) => {
      const name = this.getModifiedHeader(h.name);
      return [name, name.toLowerCase(), h.name[0] === "?"];
    });
  }

  // LSEG: BaseResponseProcessor_63223.getModifiedHeaders
  getModifiedHeaders(headers: ResponseHeader[], keepCase = false): string[] {
    return headers.map((h) => {
      const name = this.getModifiedHeader(h.name);
      return keepCase ? name : name.toLowerCase();
    });
  }

  // LSEG: BaseResponseProcessor_63223.getModifiedHeader
  getModifiedHeader(name: string): string {
    return name[0] === "?" || name[0] === "!" ? name.substring(1) : name;
  }
}

const PROCESSING_INFORMATION = "processingInformation";
const PROCESSING_INFORMATION_ALL_ROWS = "processingInformationAllRows";

/** A response's headers and its rows of values, one value per header. */
export interface HeadedRows {
  headers: ResponseHeader[];
  data: unknown[][];
}

/**
 * The rows a function lays out one per request label (FX tenors, currency
 * codes): the labels in request order, the answer's key for them, and
 * whether the user's Fields keep that key's column.
 */
// LSEG: the `{ labels, rowLabel, keyFieldExist }` object of SwapToSwapResProcessor_15334 (and its siblings).getDataFromResponse
export interface RowLabels {
  labels: unknown[];
  rowLabel: string;
  keyFieldExist: boolean | undefined;
}

/** An answer row: the answer's keys and their values. */
export type AnswerRow = Record<string, unknown>;

/**
 * An AdFin function's response processor. A subclass names its columns
 * (`getHeaders`) and reads LSEG's answer into rows (`getDataFromResponse`);
 * this class picks the fields, applies the display options and handles errors.
 */
// LSEG: BaseAdFinResProcessor_15646.BaseAdFinResProcessor
export abstract class BaseAdFinResProcessor extends BaseResponseProcessor {
  /** The two processing-information columns, required ("!") so they are always read; empty ones are dropped later. */
  // LSEG: BaseAdFinResProcessor_15646.getHeaders
  getHeaders(): ResponseHeader[] {
    return [
      { name: `!${PROCESSING_INFORMATION_ALL_ROWS}`, type: "String" },
      { name: `!${PROCESSING_INFORMATION}`, type: "String" },
    ];
  }

  /**
   * `response` is `{ data: [item] }`; a missing one gives "Bad <fn> response syntax!".
   * `request` is the request item sent (the FX functions read their tenors or currency codes from it).
   */
  abstract getDataFromResponse(response: unknown, contract: AdFinInputContract, request?: Record<string, unknown>): ClientResponse;

  /**
   * An answer, `{ data: [item] }` or `{ error }`, as Excel gets it.
   * (LSEG's demo/show options, which print the payload and response, are not ported.)
   */
  // LSEG: BaseAdFinResProcessor_15646.response
  response(
    response: { data?: unknown[]; error?: ResponseError },
    contract: AdFinInputContract,
    request?: Record<string, unknown>,
  ): ClientResponse {
    if (response?.error !== undefined) return this.getErrorResponse({ error: response.error });
    return this.getDataFromResponse(response, contract, request);
  }

  /** `key` of the answer's item (`data[0]`, followed down while it has one), or of the answer itself. */
  // LSEG: BaseAdFinResProcessor_15646.getValidatedProperty
  getValidatedProperty(response: unknown, key: string): unknown {
    const r = response as { data?: unknown[] } | null | undefined;
    if (r?.data?.[0]) return this.getValidatedProperty(r.data[0], key);
    return (r as Record<string, unknown> | null | undefined)?.[key];
  }

  // LSEG: BaseAdFinResProcessor_15646.getValidatedArray
  getValidatedArray(v: unknown): unknown[] | null {
    return v && Array.isArray(v) ? v : null;
  }

  /** The list under `key`, each object row given the answer's processing information (when any) as processingInformationAllRows. */
  // LSEG: BaseAdFinResProcessor_15646.getValidatedDataRows
  getValidatedDataRows(response: unknown, key: string, processingInformation: unknown): unknown[] | null {
    const rows = this.getValidatedArray(this.getValidatedProperty(response, key));
    if (!rows || !processingInformation) return rows;
    return rows.map((row) =>
      row && typeof row === "object" ? { ...row, [PROCESSING_INFORMATION_ALL_ROWS]: processingInformation } : row,
    );
  }

  /** An answer row as values in the headers' order, "" for a missing one. */
  // LSEG: BaseAdFinResProcessor_15646.buildRowData
  buildRowData(row: unknown): unknown[] {
    return this.getModifiedHeaders(this.getHeaders(), true).map((name) => (row as AnswerRow | null | undefined)?.[name] ?? "");
  }

  /**
   * One row per label, in the labels' order: the answer's row with that
   * label, else its first row with none, else an empty row; each row gets
   * the label. Without labels, the answer's rows as they are.
   * (Like LSEG, it fails when there are labels but no rows.)
   */
  // LSEG: BaseAdFinResProcessor_15646.getDataRowsInOrder
  getDataRowsInOrder(order: RowLabels, rows: unknown[] | null): unknown[] | null {
    if (order.labels.length === 0) return rows;
    const out: unknown[] = [];
    const key = `${order.rowLabel}`;
    order.labels.forEach((label) => {
      const list = rows as AnswerRow[] | null;
      let row = list?.find((r) => r[key] === label);
      if (!row) {
        const i = list?.findIndex((r) => !r[key]);
        if (i !== -1) row = (list as AnswerRow[]).splice(i as number, 1)[0];
      }
      if (!row) row = {};
      row[key] = label;
      out.push(row);
    });
    return out;
  }

  /** An empty list, or an object with no values. */
  // LSEG: BaseAdFinResProcessor_15646.isEmptyObject
  isEmptyObject(v: unknown): boolean {
    if (!v) return false;
    if (Array.isArray(v) && v.length === 0) return true;
    return typeof v === "object" && Object.values(v as object).length === 0;
  }

  /** Whether the answer has `key` (in itself or its item), or is empty. */
  // LSEG: BaseAdFinResProcessor_15646.checkValidResponse
  checkValidResponse(response: unknown, key: string): boolean {
    const r = response as { data?: unknown[] } & Record<string, unknown>;
    return (
      this.isEmptyObject(r) ||
      !!r?.[key] ||
      this.isEmptyObject(r?.data) ||
      this.isEmptyObject(r?.data?.[0]) ||
      !!(r?.data?.[0] as Record<string, unknown> | undefined)?.[key]
    );
  }

  /** "Bad <fn> response syntax! Expected '<key>', but received:<the answer, shallowly>". */
  // LSEG: BaseAdFinResProcessor_15646.getBadResponse
  getBadResponse(fn: string, key: string, response: unknown): ClientResponse {
    const received = getShallowStringify(response);
    return this.getErrorResponse({
      error: { status: "", errors: [{ key: "", reason: `Bad ${fn} response syntax! Expected '${key}', but received:${received}` }] },
    });
  }

  /** A single reason becomes the message. */
  // LSEG: BaseAdFinResProcessor_15646.getErrorResponse
  getErrorResponse(e: { error: ResponseError }): ClientResponse {
    const error: ResponseError = { errors: [], status: "", ...e.error };
    if (error.errors!.length === 1 && error.errors![0].reason) {
      error.message = error.errors![0].reason;
      error.errors = [];
    }
    return super.getErrorResponse({ error });
  }

  /** "Bad <fn> response syntax!", for a missing answer. */
  // LSEG: Date*ResProcessor.getDataFromResponse (the `!e` branch, the same in all seven)
  protected badSyntax(fn: string): ClientResponse {
    return this.getErrorResponse({ error: { status: "", errors: [{ key: "", reason: `Bad ${fn} response syntax!` }] } });
  }

  /** The fields to show: the user's Fields against this processor's headers. */
  // LSEG: BaseAdFinResProcessor_15646.getFields
  getFields(contract: AdFinInputContract, headers: ResponseHeader[]): string[] {
    const names: string[] = [];
    headers.forEach((h) => {
      if (h?.name) names.push(h.name);
    });
    return getOutputFields(contract.fields ?? [], names);
  }

  // LSEG: BaseAdFinResProcessor_15646.getRequestIds
  getRequestIds(data: unknown[]): unknown[] {
    return new Array(data.length).fill(null);
  }

  /** "" for an empty value, and for any text containing "null" (any case). */
  // LSEG: BaseAdFinResProcessor_15646.getValueIfEmpty
  getValueIfEmpty(v: unknown): unknown {
    return String(v) === "" || v === null || v === undefined || /null/i.test(String(v)) ? "" : v;
  }

  /** The values of `fields` per row, and whether the processing-information columns have any. */
  // LSEG: BaseAdFinResProcessor_15646.getValueSetsWithPI
  getValueSetsWithPI(fields: string[], rows: HeadedRows): { valueSets: unknown[][]; hasPI: boolean; hasAllRowsPI: boolean } {
    const indexes = this.getFieldsIndexes(fields, rows);
    const piIndex = this.getFieldsIndexes([PROCESSING_INFORMATION], rows)[0];
    const allRowsPiIndex = this.getFieldsIndexes([PROCESSING_INFORMATION_ALL_ROWS], rows)[0];
    let hasPI = false;
    let hasAllRowsPI = false;
    const valueSets = rows.data.map((row) =>
      indexes.map((i) => {
        const v = this.getValueIfEmpty(row[i]);
        if (v !== "") {
          if (i === piIndex) hasPI = true;
          if (i === allRowsPiIndex) hasAllRowsPI = true;
        }
        return v;
      }),
    );
    return { valueSets, hasPI, hasAllRowsPI };
  }

  /**
   * Each field as its header spells it (capitalized); a field with no header
   * keeps its own name. The label column is left out when the user's Fields do not keep it.
   */
  // LSEG: BaseAdFinResProcessor_15646.getFieldsNames
  getFieldsNames(fields: string[], rows: HeadedRows, order?: RowLabels): string[] {
    if (!rows?.headers?.length || !fields?.length) return [];
    const names: string[] = [];
    const headers = this.getHeaderResponse(rows.headers);
    fields.forEach((field) => {
      const lc = field.toLowerCase();
      const prefix = `${lc}.`;
      if (order?.keyFieldExist === false && order.rowLabel.toLowerCase() === lc) return;
      let found = false;
      headers.forEach(([name, nameLC, optional]) => {
        if (nameLC === lc || (nameLC.startsWith(prefix) && !optional)) {
          found = true;
          names.push(capitalize(name));
        }
      });
      if (!found) names.push(capitalize(field));
    });
    return names;
  }

  /** Each field's column in the rows (-1 when none). */
  // LSEG: BaseAdFinResProcessor_15646.getFieldsIndexes
  getFieldsIndexes(fields: string[], rows: HeadedRows): number[] {
    if (!rows?.headers?.length || !fields?.length) return [];
    const headers = this.getModifiedHeaders(rows.headers);
    return fields.map((f) => headers.findIndex((h) => h === f.toLowerCase()));
  }

  /** The rows laid out for Excel: fields picked, display options and TRANSPOSE applied, empty rows removed. */
  // LSEG: BaseAdFinResProcessor_15646.getDataRows
  getDataRows(rows: HeadedRows, contract: AdFinInputContract, order?: RowLabels): ClientResponse {
    const options = `${contract.args[contract.argIndexMap?.options ?? -1] ?? ""}`;
    const [option] = this.getDisplayOptions(options, "id", "fd");
    const fields = this.getFieldsNames(this.getFields(contract, rows.headers), rows, order);
    const { valueSets, hasPI, hasAllRowsPI } = this.getValueSetsWithPI(fields, rows);
    const values = this.evaluateProcessInformation({ requestIDs: this.getRequestIds(rows.data), fields, valueSets }, hasPI, hasAllRowsPI);
    const laid = this.transposeData(this.rhChFormat(values, option), options);
    return this.removeEmptyRows(this.clientFormat(laid), true);
  }

  /** The processing-information columns: dropped when empty, otherwise shown under a blank header. */
  // LSEG: BaseAdFinResProcessor_15646.evaluateProcessInformation
  evaluateProcessInformation(values: FieldValues, hasPI: boolean, hasAllRowsPI: boolean): FieldValues {
    const fields: string[] = [];
    let piIndex = -1;
    let allRowsPiIndex = -1;
    values.fields.forEach((field, i) => {
      const lc = field.toLowerCase();
      if (lc === PROCESSING_INFORMATION.toLowerCase()) {
        if (hasPI) fields.push("");
        else piIndex = i;
      } else if (lc === PROCESSING_INFORMATION_ALL_ROWS.toLowerCase()) {
        if (hasAllRowsPI) fields.push("");
        else allRowsPiIndex = i;
      } else {
        fields.push(field);
      }
    });
    const valueSets = values.valueSets.map((set) => set.filter((_, i) => i !== piIndex && i !== allRowsPiIndex));
    return { requestIDs: values.requestIDs, fields, valueSets };
  }
}
